import axios from 'axios';
import api, { API_BASE_URL } from '@/lib/api';
import { CreateShareLinkResponse, Event, EventChangeLogPage, EventType, SharedEvent } from '@/types';

export interface CreateEventPayload {
    date: string;
    projectId: string;
    text: string;
    type: EventType;
    tagIds?: string[];
}

export interface UpdateEventPayload {
    date?: string;
    text?: string;
    type?: EventType;
    tagIds?: string[];
    // Audit metadata for the regular edit flow only — never sent by draft/create/publish paths.
    changeReason?: string;
}

export const EventService = {
    getEventsByProjectId: async (projectId: string): Promise<Event[]> => {
        const { data } = await api.get(`/events/${projectId}`, {
            headers: {
                Authorization: `Bearer ${window.localStorage.getItem('fieldOsAccessToken')}`
            }
        });
        return data;
    },
    createEvent: async (eventData: CreateEventPayload) => {
        const { data } = await api.post('/events', eventData, {
            headers: {
                Authorization: `Bearer ${window.localStorage.getItem('fieldOsAccessToken')}`
            }
        });
        return data;
    },
    updateEvent: async (eventId: string, payload: UpdateEventPayload) => {
        const { data } = await api.patch(`/events/${eventId}`, payload, {
            headers: {
                Authorization: `Bearer ${window.localStorage.getItem('fieldOsAccessToken')}`
            }
        });
        return data;
    },
    // Draft lifecycle: create mode auto-saves a DRAFT event behind the scenes, then publishes it.
    createDraftEvent: async (payload: { projectId: string }) => {
        const { data } = await api.post('/events/draft', payload, {
            headers: {
                Authorization: `Bearer ${window.localStorage.getItem('fieldOsAccessToken')}`
            }
        });
        return data;
    },
    updateDraftEvent: async (eventId: string, payload: UpdateEventPayload) => {
        const { data } = await api.patch(`/events/${eventId}/draft`, payload, {
            headers: {
                Authorization: `Bearer ${window.localStorage.getItem('fieldOsAccessToken')}`
            }
        });
        return data;
    },
    publishDraftEvent: async (eventId: string) => {
        const { data } = await api.post(`/events/${eventId}/publish`, undefined, {
            headers: {
                Authorization: `Bearer ${window.localStorage.getItem('fieldOsAccessToken')}`
            }
        });
        return data;
    },
    getDraftEvent: async (eventId: string) => {
        const { data } = await api.get(`/events/${eventId}/draft`, {
            headers: {
                Authorization: `Bearer ${window.localStorage.getItem('fieldOsAccessToken')}`
            }
        });
        return data;
    },
    getEventChangeLogs: async (eventId: string, page = 1, pageSize = 50): Promise<EventChangeLogPage> => {
        const { data } = await api.get(`/events/${eventId}/change-logs`, {
            params: { grouped: true, page, pageSize },
            headers: {
                Authorization: `Bearer ${window.localStorage.getItem('fieldOsAccessToken')}`
            }
        });
        return data;
    },
    generateShareLink: async (eventId: string): Promise<CreateShareLinkResponse> => {
        const { data } = await api.post(`/events-share/${eventId}/share-link`);
        return data;
    },
    // Bare axios on purpose — bypasses the auth interceptor so a guest with stale tokens
    // in localStorage doesn't get bounced through /auth/refresh → /login on a public link.
    getSharedEvent: async (token: string): Promise<SharedEvent> => {
        const { data } = await axios.get(`${API_BASE_URL}/shared-events/${encodeURIComponent(token)}`);
        return data;
    },
    // Bare axios on purpose — same public-link rationale as getSharedEvent: a guest must be
    // able to answer without the auth interceptor bouncing them through /auth/refresh → /login.
    answerSharedEvent: async (shareToken: string, answer: string): Promise<void> => {
        await axios.post(
            `${API_BASE_URL}/shared-events/${encodeURIComponent(shareToken)}/answer`,
            { answer }
        );
    },
}
